import { useRef, useState, useEffect, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ActionSection,
  ScrollableContent,
  RupeeIcon,
  ModalHeader,
} from "../Styles/InputStyles";
import { MdClose, MdEdit } from "react-icons/md";
import tw from "tailwind-styled-components";
import "react-datepicker/dist/react-datepicker.css";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import SelectOptionModal from "../SelectOptionModal";
import { ProfileSection } from "../Styles/PageStyles";
import moment from "moment";
import Config, { paidByOptions, editStageOptions, paidByRent } from "../../Config";
import { FaRegBuilding, FaRegCalendar } from "react-icons/fa";
import { capitalizeFirstLetter } from "../../helpers";

const EditDeal = ({
  handleClose,
  key,
  selectedData,
  onSubmit,
  editDealMutate,
  editDealLoading,
}) => {
  let formikRef = useRef(null);
  const [height, setHeight] = useState(window.innerHeight);
  const [isStageModalOpen, setIsStageModalOpen] = useState(false);

  const stageOption = editStageOptions.map((role) => ({
    label: role.label,
    value: role.value,
  }));

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    const handleResize = () => {
      setHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [key]);

  // Parse selectedData to populate initial values
  const getInitialValues = () => {
    if (!selectedData) {
      return {
        clientName: "",
        clientMobile: "",
        status: "",
        followUpDate: null,
        // time: "",
        note: "",
        commissionAmount: "",
        commissionPaidBy: "",
        buyerCommission: "",
        sellerCommission: "",
      };
    }

    // Parse follow-up date and time
    let followupDate = null;
    let followupTime = "";

    if (selectedData.followUpDate) {
      const dateObj = new Date(selectedData.followUpDate);
      followupDate = dateObj;
      followupTime = dateObj.toTimeString().slice(0, 5); // HH:MM format
    }

    return {
      clientName: selectedData.clientName || "",
      clientMobile: selectedData.clientMobile || "",
      status: selectedData.status || "",
      followUpDate: followupDate,
      followupTime: followupTime,
      note: selectedData.note || "",
      commissionAmount: selectedData.commissionAmount || "",
      commissionPaidBy: selectedData.commissionPaidBy || "",
      buyerCommission: selectedData.buyerCommission || "",
      sellerCommission: selectedData.sellerCommission || "",

      tenantBudget:
        Number(selectedData?.tenantBudget) > 0
          ? Number(selectedData?.tenantBudget)
          : "",
      ownerExpected:
        Number(selectedData?.ownerExpected) > 0
          ? Number(selectedData?.ownerExpected)
          : "",
      negotiationStatus: selectedData?.negotiationStatus || "",

      monthlyRent:
        Number(selectedData?.monthlyRent) > 0
          ? Number(selectedData?.monthlyRent)
          : "",

      ownerPayout:
        Number(selectedData?.ownerShare) > 0
          ? Number(selectedData?.ownerShare)
          : "",

      securityDeposit:
        Number(selectedData?.securityDeposit) > 0
          ? Number(selectedData?.securityDeposit)
          : "",

      dueDate: selectedData?.dueDate ? new Date(selectedData.dueDate) : null,

      agreementPeriod:
        Number(selectedData?.agreementPeriod) > 0
          ? Number(selectedData?.agreementPeriod)
          : "",
    };
  };

  // Base validation schema
  const baseValidationSchema = Yup.object({
    status: Yup.number().required("Stage is required"),
  });

  const validationSchema = baseValidationSchema
    .shape({
      followUpDate: Yup.date().when("status", {
        is: (val) =>
          val &&
          (val.toString() === Config.DEAL_STATUS.VISIT_SCHEDULED ||
            val.toString() === Config.DEAL_STATUS.INQUIRY ||
            val.toString() === Config.DEAL_STATUS.NEGOTIATION),
        then: (schema) =>
          schema
            .nullable()
            .required("Follow-up date is required for this stage"),
        otherwise: (schema) => schema.nullable(),
      }),

       // ---------------- Brokerage ----------------

      // commissionAmount: Yup.number().when("status", {
      //   is: (val) => val && val.toString() === Config.DEAL_STATUS.BOOKED,
      //   then: (schema) =>
      //     schema
      //       .required("Commission amount is required")
      //       .positive("Invalid amount"),
      //   otherwise: (schema) => schema.nullable(),
      // }),

      commissionPaidBy: Yup.number().when("status", {
        is: (status) =>
            Number(status) === Number(Config.DEAL_STATUS.BOOKED),
        then: (schema) => schema.required("Paid by is required"),
        otherwise: (schema) => schema.nullable(),
      }),

      buyerCommission: Yup.number().when(["status", "commissionPaidBy"], {
        is: (status, commissionPaidBy) =>
          status &&
          Number(status) === Number(Config.DEAL_STATUS.BOOKED)&&
          (commissionPaidBy === Config.PAID_BY_TYPE.BUYER ||
            commissionPaidBy === Config.PAID_BY_TYPE.BOTH),
        then: (schema) =>
          schema
            .typeError("Amount is required")
            .required("Amount is required")
            .min(0, "Amount cannot be negative"),
        otherwise: (schema) => schema.nullable(),
      }),

      sellerCommission: Yup.number().when(["status", "commissionPaidBy"], {
        is: (status, commissionPaidBy) =>
          status &&
          Number(status) === Number(Config.DEAL_STATUS.BOOKED) &&
          (commissionPaidBy === Config.PAID_BY_TYPE.SELLER ||
            commissionPaidBy === Config.PAID_BY_TYPE.BOTH),
        then: (schema) =>
          schema
            .typeError("Amount is required")
            .required("Amount is required")
            .min(0, "Amount cannot be negative"),
        otherwise: (schema) => schema.nullable(),
      }),


      // ---------------- Negotiation ----------------
      tenantBudget: Yup.number().when("status", {
        is: (val) => Number(val) === Number(Config.DEAL_STATUS.NEGOTIATION),
        then: (schema) =>
          schema
            .typeError("Tenant budget is required")
            .required("Tenant budget is required")
            .min(500, "Budget must be at least ₹500")
            .max(1000000, "Budget cannot exceed ₹10,00,000"),
        otherwise: (schema) => schema.nullable(),
      }),

      ownerExpected: Yup.number().when(["status", "tenantBudget"], {
        is: (status, tenantBudget) =>
          Number(status) === Number(Config.DEAL_STATUS.NEGOTIATION) &&
          !!tenantBudget,
        then: (schema) =>
          schema
            .typeError("Owner expectation is required")
            .required("Owner expectation is required")
            .min(
              Yup.ref("tenantBudget"),
              "Owner expectation cannot be less than tenant budget",
            )
            .max(1000000, "Amount cannot exceed ₹10,00,000"),
        otherwise: (schema) => schema.nullable(),
      }),

      // ---------------- Lease ----------------
      monthlyRent: Yup.number().when("status", {
        is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
        then: (schema) =>
          schema
            .typeError("Tenant rent is required")
            .required("Tenant rent is required")
            .min(500, "Rent must be at least ₹500")
            .max(1000000, "Rent cannot exceed ₹10,00,000"),
        otherwise: (schema) => schema.nullable(),
      }),

      ownerPayout: Yup.number().when("status", {
        is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
        then: (schema) =>
          schema
            .typeError("Owner payout is required")
            .required("Owner payout is required")
            .min(0, "Owner payout cannot be negative")
            .max(1000000, "Owner payout cannot exceed ₹10,00,000"),
        otherwise: (schema) => schema.nullable(),
      }),

      securityDeposit: Yup.number().when("status", {
        is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
        then: (schema) =>
          schema
            .typeError("Security deposit is required")
            .required("Security deposit is required")
            .min(0, "Security deposit cannot be negative")
            .max(5000000, "Deposit cannot exceed ₹50,00,000"),
        otherwise: (schema) => schema.nullable(),
      }),

      dueDate: Yup.date().when("status", {
        is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
        then: (schema) => schema.nullable().required("Due date is required"),
        otherwise: (schema) => schema.nullable(),
      }),

      agreementPeriod: Yup.number().when("status", {
        is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
        then: (schema) =>
          schema
            .typeError("Upcoming rent count is required")
            .required("No. of upcoming rent is required")
            .min(1, "Minimum 1 rent cycle required")
            .max(36, "Maximum 36 months allowed"),
        otherwise: (schema) => schema.nullable(),
      }),
    })
    .test(
      "commission-total-check",
      "Total received amount cannot be greater than commission amount",
      function (values) {
        if (
          !values.status ||
          values.status.toString() !== Config.DEAL_STATUS.BOOKED
        )
          return true;

        const total =
          (Number(values.buyerCommission) || 0) +
          (Number(values.sellerCommission) || 0);

        return total <= Number(values.commissionAmount || 0);
      },
    )
    .test(
      "lease-margin-check",
      "Owner payout cannot be greater than tenant rent",
      function (values) {
        if (
          !values.status ||
          values.status.toString() !== Config.DEAL_STATUS.ACTIVE_LEASE
        )
          return true;

        const monthlyRent = Number(values.monthlyRent || 0);
        const ownerPayout = Number(values.ownerPayout || 0);

        return ownerPayout <= monthlyRent;
      },
    );

  const handleSubmit = (values, formikHelpers) => {
    let followUpDate = null;

    if (values.followUpDate) {
      followUpDate = moment(values.followUpDate).format("YYYY-MM-DD HH:mm:ss");
    }

    editDealMutate({
      dealId: selectedData?.id,
      stage: values.status,
      budget: selectedData.budget,
      date: followUpDate,
      note: values.note,

      // Brokerage
      commissionPaidBy: values.commissionPaidBy || null,
      buyerCommission: values.buyerCommission || null,
      sellerCommission: values.sellerCommission || null,

      // Financials
      monthlyRent: values.monthlyRent,
      ownerShare: values.ownerPayout,

      // Lease Terms
      securityDeposit: values.securityDeposit,
      agreementPeriod: values.agreementPeriod,
      dueDate: values.dueDate
        ? moment(values.dueDate).format("YYYY-MM-DD")
        : null,
      rentalCycle: values.dueDate ? moment(values.dueDate).format("DD") : null,
      moveInDate: values.dueDate
        ? moment(values.dueDate).format("YYYY-MM-DD")
        : null,
      agreementStartDate: values.dueDate
        ? moment(values.dueDate).format("YYYY-MM-DD")
        : null,
      dealName: selectedData?.name || "",
      dealMobile: selectedData?.mobile,

      //Negotitation Data
      tenantBudget: values.tenantBudget || null,
      ownerExpected: values.ownerExpected || null,
      negotiationStage: values.negotiationStatus || null,
    });
    // handleClose();
  };
  // Map negotiation status to number
  const DEAL_NEGOTIATION_STATUS = {
    ONGOING: 1,
    TENANT_AGREED: 2,
    OWNER_AGREED: 3,
    FINAL_DISCUSSION: 4,
  };

  const isRental = selectedData?.listingType === 1;
  const { buyerLabel, sellerLabel } =
    useMemo(() => {
      if (isRental) {
        return {
          buyerLabel: "Tenant",
          sellerLabel: "Owner",
        };
      }

      return {
        buyerLabel: "Buyer",
        sellerLabel: "Seller",
      };
    }, [isRental]);
  const paidByList = useMemo(() => {
    return isRental ? paidByRent : paidByOptions;
  }, [isRental]);

  return (
    <Container style={{ height: `${height}px`, overflow: "auto" }}>
      <ModalHeader>
        <h1 className="text-white text-lg font-medium">Edit Deal</h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </ModalHeader>

      <ProfileSection className="px-4 py-3 flex items-center justify-between">
        {/* Left: Icon + Info */}
        <div className="flex items-center gap-2">
          {/* Inventory Icon */}
          <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
            <FaRegBuilding className="text-gray-600" />
          </div>

          {/* Text Info */}
          <div className="flex flex-col">
            <p className="text-md font-medium text-gray-800">
              {capitalizeFirstLetter(selectedData?.name || "")}
            </p>
            <p className="text-xs text-gray-400">
              {capitalizeFirstLetter(selectedData?.inventoryName || "")}
            </p>
          </div>
        </div>

        {/* Right: Budget */}
        <div className="text-right">
          <p className="text-xs text-gray-400">Budget</p>
          <p className="text-lg font-bold text-green-600">
            ₹{selectedData?.budget || 0}
          </p>
        </div>
      </ProfileSection>

      <div className="px-6 h-full flex flex-col justify-between">
        <Formik
          innerRef={formikRef}
          initialValues={getInitialValues()}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          validateOnChange={true}
          validateOnBlur={true}
          enableReinitialize={true}
        >
          {({
            values,
            setFieldValue,
            handleChange,
            handleBlur,
            errors,
            touched,
            resetForm,
          }) => {
            const getCurrentStageLabel = () => {
              const selectedStage = editStageOptions.find(
                (role) => role.value === values?.status,
              );
              return selectedStage ? selectedStage.label : "Select stage";
            };

            const handleStageSelect = (selectedValue) => {
              setFieldValue("status", selectedValue);
              setIsStageModalOpen(false);

              setFieldValue("note", "");

              // Reset commission fields if stage is not Booked
              if (selectedValue !== Config.DEAL_STATUS.BOOKED) {
                setFieldValue("commissionAmount", "");
                setFieldValue("commissionPaidBy", "");
                setFieldValue("buyerCommission", "");
                setFieldValue("sellerCommission", "");
              }

              // Reset time field if stage is not Visit Scheduled
              if (selectedValue !== Config.DEAL_STATUS.VISIT_SCHEDULED) {
                setFieldValue("followupTime", "");
              }
            };

            return (
              <Form>
                <ScrollableContent className="max-h-[calc(100vh-180px)]">
                  <Wrapper>
                    {/* Stage with Selection Modal */}
                    <InputGroup>
                      <Label htmlFor="status">Stage *</Label>
                      <FieldWrappers onClick={() => setIsStageModalOpen(true)}>
                        <Field
                          type="text"
                          name="status"
                          id="status"
                          autoComplete="off"
                          readOnly
                          value={getCurrentStageLabel()}
                          className="w-full border-none outline-none bg-transparent cursor-pointer"
                        />
                        <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                      </FieldWrappers>
                      {errors.status && touched.status && (
                        <ErrorText>{errors.status}</ErrorText>
                      )}
                    </InputGroup>

                    {/* Follow-up Date - Required for Inquiry, Visit Scheduled, Negotiation */}
                    {(values.status === Config.DEAL_STATUS.INQUIRY ||
                      values.status === Config.DEAL_STATUS.VISIT_SCHEDULED ||
                      values.status === Config.DEAL_STATUS.NEGOTIATION) && (
                      <>
                        <InputGroup>
                          <Label htmlFor="followUpDate">
                            Follow-up / Visit Date *
                          </Label>
                          <FieldWrappers>
                            <DatePicker
                              selected={values.followUpDate}
                              onChange={(date) =>
                                setFieldValue("followUpDate", date)
                              }
                              showTimeSelect
                              timeIntervals={15}
                              timeCaption="Time"
                              dateFormat="dd MMM yyyy h:mm aa"
                              minDate={new Date()}
                              placeholderText="Select follow-up date & time"
                              className="w-full"
                              calendarClassName="custom-datepicker"
                              popperClassName="custom-datepicker-popper"
                              popperPlacement="bottom-start"
                              portalId="root"
                            />
                          </FieldWrappers>
                          {errors.followUpDate && touched.followUpDate && (
                            <ErrorText>{errors.followUpDate}</ErrorText>
                          )}
                        </InputGroup>
                      </>
                    )}

                    {values.status === Config.DEAL_STATUS.NEGOTIATION && (
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-700">
                            Negotiation Details
                          </p>
                        </div>

                        {/* Rent Expectation */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="mb-1 block text-xs">
                              Tenant Budget
                            </Label>
                            <FieldWrappers>
                              <RupeeIcon />
                              <Field
                                type="number"
                                name="tenantBudget"
                                placeholder="Tenant offer"
                                className="pl-5 w-full"
                              />
                            </FieldWrappers>
                            {errors.tenantBudget && touched.tenantBudget && (
                              <ErrorText className="mt-1">
                                {errors.tenantBudget}
                              </ErrorText>
                            )}
                          </div>

                          <div>
                            <Label className="mb-1 block text-xs">
                              Owner Expectation
                            </Label>
                            <FieldWrappers>
                              <RupeeIcon />
                              <Field
                                type="number"
                                name="ownerExpected"
                                placeholder="Owner demand"
                                className="pl-5 w-full"
                              />
                            </FieldWrappers>
                            {errors.ownerExpected && touched.ownerExpected && (
                              <ErrorText className="mt-1">
                                {errors.ownerExpected}
                              </ErrorText>
                            )}
                          </div>
                        </div>

                        {/* Gap Indicator */}
                        {values.tenantBudget != null &&
                          values.ownerExpected != null && (
                            <div className="bg-white border rounded-lg p-3 text-sm">
                              <span className="text-gray-500">
                                Negotiation Gap
                              </span>
                              <p className="text-orange-600 font-semibold">
                                ₹{" "}
                                {Math.abs(
                                  (values.ownerExpected || 0) -
                                    (values.tenantBudget || 0),
                                )}
                              </p>
                            </div>
                          )}

                        <div>
                          <Label className="mb-1 block text-xs">
                            Negotiation Status
                          </Label>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              "ONGOING",
                              "OWNER_AGREED",
                              "TENANT_AGREED",
                              "FINAL_DISCUSSION",
                            ].map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() =>
                                  setFieldValue(
                                    "negotiationStatus",
                                    DEAL_NEGOTIATION_STATUS[option],
                                  )
                                }
                                className={`py-2 rounded-lg text-sm font-medium transition
                                                                ${
                                                                  values.negotiationStatus ===
                                                                  DEAL_NEGOTIATION_STATUS[
                                                                    option
                                                                  ]
                                                                    ? "bg-primary-color text-white"
                                                                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                                                                }`}
                              >
                                {option.replace("_", " ")}{" "}
                                {/* optional: nicer display */}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Commission Fields - Only for Closed stage */}
                    {values.status === Config.DEAL_STATUS.BOOKED && (
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-4">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-700">
                            Commission
                          </p>
                          <span className="text-xs text-gray-400">
                            Required
                          </span>
                        </div>

                        {/* Paid By Selection */}
                        <div>
                          <Label className="mb-2 block">Who pays?</Label>

                          <div className="grid grid-cols-3 gap-2">
                            {paidByList.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  setFieldValue(
                                    "commissionPaidBy",
                                    option.value,
                                  );
                                  setFieldValue("buyerCommission", "");
                                  setFieldValue("sellerCommission", "");
                                }}
                                className={`py-2 rounded-lg text-sm font-medium transition-all duration-200
                                    ${
                                      values.commissionPaidBy === option.value
                                        ? "bg-primary-color text-white shadow-sm"
                                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
                                    }
                                    `}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>

                          {errors.commissionPaidBy &&
                            touched.commissionPaidBy && (
                              <ErrorText className="mt-1">
                                {errors.commissionPaidBy}
                              </ErrorText>
                            )}
                        </div>

                        {/* Amount Inputs */}
                        <div className="grid grid-cols-2 gap-3">
                          {/* Buyer */}
                          {(values.commissionPaidBy ===
                            Config.PAID_BY_TYPE.BUYER ||
                            values.commissionPaidBy ===
                              Config.PAID_BY_TYPE.BOTH) && (
                            <div>
                              <Label className="mb-1 block text-xs">
                                {buyerLabel}
                              </Label>
                              <FieldWrappers>
                                <RupeeIcon />
                                <Field
                                  type="number"
                                  name="buyerCommission"
                                  className="pl-5 w-full"
                                  placeholder="Enter commission"
                                />
                              </FieldWrappers>
                              {errors.buyerCommission &&
                                touched.buyerCommission && (
                                  <ErrorText className="mt-1">
                                    {errors.buyerCommission}
                                  </ErrorText>
                                )}
                            </div>
                          )}

                          {/* Seller */}
                          {(values.commissionPaidBy ===
                            Config.PAID_BY_TYPE.SELLER ||
                            values.commissionPaidBy ===
                              Config.PAID_BY_TYPE.BOTH) && (
                            <div>
                              <Label className="mb-1 block text-xs">
                                {sellerLabel}
                              </Label>
                              <FieldWrappers>
                                <RupeeIcon />
                                <Field
                                  type="number"
                                  name="sellerCommission"
                                  className="pl-5 w-full"
                                  placeholder="Enter commission"
                                />
                              </FieldWrappers>
                              {errors.sellerCommission &&
                                touched.sellerCommission && (
                                  <ErrorText className="mt-1">
                                    {errors.sellerCommission}
                                  </ErrorText>
                                )}
                            </div>
                          )}
                        </div>

                        {/* Validation Message */}
                        {values.commissionAmount &&
                          Number(values.buyerCommission || 0) +
                            Number(values.sellerCommission || 0) >
                            Number(values.commissionAmount) && (
                            <div className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded-md">
                              Total received amount cannot exceed commission
                              amount
                            </div>
                          )}
                      </div>
                    )}

                    {values.status === Config.DEAL_STATUS.ACTIVE_LEASE && (
                      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-4">
                        {/* Lease Financials */}
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-700">
                            Lease Financials
                          </p>
                          <span className="text-xs text-gray-400">Monthly</span>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="mb-1 block text-xs">
                              Tenant Rent
                            </Label>
                            <FieldWrappers>
                              <RupeeIcon />
                              <Field
                                type="number"
                                name="monthlyRent"
                                placeholder="Enter rent"
                                className="pl-5 w-full"
                              />
                            </FieldWrappers>
                            {errors.monthlyRent && touched.monthlyRent && (
                              <ErrorText>{errors.monthlyRent}</ErrorText>
                            )}
                          </div>

                          <div>
                            <Label className="mb-1 block text-xs">
                              Owner Payout
                            </Label>
                            <FieldWrappers>
                              <RupeeIcon />
                              <Field
                                type="number"
                                name="ownerPayout"
                                placeholder="Enter payout"
                                className="pl-5 w-full"
                              />
                            </FieldWrappers>
                            {errors.ownerPayout && touched.ownerPayout && (
                              <ErrorText>{errors.ownerPayout}</ErrorText>
                            )}
                          </div>
                        </div>

                        {/* Auto Margin */}
                        <div className="bg-white border rounded-lg p-3 text-sm">
                          <span className="text-gray-500">Your Margin</span>
                          <p className="text-green-600 font-semibold">
                            ₹{" "}
                            {(values.monthlyRent || 0) -
                              (values.ownerPayout || 0)}
                          </p>
                        </div>

                        {/* -------- Lease Terms -------- */}
                        <div className="border-t pt-4 space-y-4">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-semibold text-gray-700">
                              Lease Terms
                            </p>
                            <span className="text-xs text-gray-400">
                              Required
                            </span>
                          </div>

                          {/* Security Deposit */}
                          <div>
                            <Label className="mb-1 block text-xs">
                              Security Deposit
                            </Label>
                            <FieldWrappers>
                              <RupeeIcon />
                              <Field
                                type="number"
                                name="securityDeposit"
                                placeholder="Enter deposit"
                                className="pl-5 w-full"
                              />
                            </FieldWrappers>
                            {errors.securityDeposit &&
                              touched.securityDeposit && (
                                <ErrorText>{errors.securityDeposit}</ErrorText>
                              )}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            {/* Due Date */}
                            <div>
                              <Label>Due Date</Label>
                              <FieldWrappers>
                                <DatePicker
                                  selected={values.dueDate}
                                  onChange={(d) => setFieldValue("dueDate", d)}
                                  dateFormat="yyyy-MM-dd"
                                  placeholderText="Select date"
                                  className="cursor-pointer items-center"
                                  showIcon
                                  icon={
                                    <FaRegCalendar className="text-gray-400 text-xs mt-0.5" />
                                  }
                                />
                              </FieldWrappers>
                              {errors.dueDate && touched.dueDate && (
                                <ErrorText>{errors.dueDate}</ErrorText>
                              )}
                            </div>

                            {/* No. of Upcoming Payments */}
                            <div>
                              {/* <Label className="mb-1 block text-xs">No. of Rent Payments</Label>
                                                            <FieldWrappers>
                                                                <Field
                                                                    type="number"
                                                                    name="agreementPeriod"
                                                                    placeholder="e.g. 6"
                                                                    className="w-full"
                                                                    min="1"
                                                                />
                                                            </FieldWrappers> */}
                              <Label>No. of Upcoming Rent</Label>
                              <FieldWrappers>
                                <Field
                                  type="number"
                                  name="agreementPeriod"
                                  placeholder="Enter months"
                                  className="w-full"
                                />
                              </FieldWrappers>
                              {errors.agreementPeriod &&
                                touched.agreementPeriod && (
                                  <ErrorText>
                                    {errors.agreementPeriod}
                                  </ErrorText>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <InputGroup>
                      <Label htmlFor="note">
                        {values.status === Config.DEAL_STATUS.LOST
                          ? "Reason *"
                          : "Notes"}
                      </Label>
                      <Textarea
                        id="note"
                        name="note"
                        className="w-full h-20"
                        placeholder="Enter your remarks..."
                        value={values.note}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      {errors.note && touched.note && (
                        <ErrorText>{errors.note}</ErrorText>
                      )}
                    </InputGroup>
                  </Wrapper>
                </ScrollableContent>

                <ActionSection>
                  <SideCancelBtn
                    type="button"
                    onClick={() => {
                      resetForm();
                      handleClose();
                    }}
                  >
                    Cancel
                  </SideCancelBtn>
                  <SubmitBtn type="submit">
                    {editDealLoading ? "Updating..." : "Update"}
                  </SubmitBtn>
                </ActionSection>

                <SelectOptionModal
                  isOpen={isStageModalOpen}
                  onClose={() => setIsStageModalOpen(false)}
                  title="Select Stage"
                  options={stageOption}
                  selectedValue={values.status}
                  onSelect={handleStageSelect}
                  searchPlaceholder="Search stage..."
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    </Container>
  );
};

const Container = tw.div`bg-white w-full h-full flex flex-col`;
const Textarea = tw.textarea`w-full h-24 border rounded-xl p-2`;
const ErrorText = tw.div`text-red-500 text-xs`;

export default EditDeal;
















// import { useRef, useState, useEffect } from "react";
// import { Formik, Field, Form } from "formik";
// import {
//   InputGroup,
//   FieldWrappers,
//   Label,
//   SubmitBtn,
//   SideCancelBtn,
//   SelectionCard,
//   Wrapper,
//   ActionSection,
//   ScrollableContent,
//   CardWrapper,
//   RupeeIcon,
//   ModalHeader,
// } from "../Styles/InputStyles";
// import { MdClose, MdEdit } from "react-icons/md";
// import tw from "tailwind-styled-components";
// import "react-datepicker/dist/react-datepicker.css";
// import * as Yup from "yup";
// import DatePicker from "react-datepicker";
// import SelectOptionModal from "../SelectOptionModal";
// import { ProfileSection } from "../Styles/PageStyles";
// import moment from "moment";
// import Config, { paidByOptions, editStageOptions } from "../../Config";
// import { FaRegBuilding, FaRegCalendar } from "react-icons/fa";
// import { capitalizeFirstLetter } from "../../helpers";
// import { toast } from "react-toastify";

// const EditDeal = ({
//   handleClose,
//   key,
//   selectedData,
//   onSubmit,
//   editDealMutate,
//   editDealLoading,
// }) => {
//   let formikRef = useRef(null);
//   const [height, setHeight] = useState(window.innerHeight);
//   const [isStageModalOpen, setIsStageModalOpen] = useState(false);

//   const priorities = [
//     { label: "Hot", color: "bg-red-50 text-red-600 border-red-200" },
//     { label: "Warm", color: "bg-yellow-50 text-yellow-600 border-yellow-200" },
//     { label: "Cold", color: "bg-blue-50 text-blue-600 border-blue-200" },
//   ];

//   const stageOption = editStageOptions.map((role) => ({
//     label: role.label,
//     value: role.value,
//   }));

//   useEffect(() => {
//     if (formikRef.current) {
//       formikRef.current.resetForm();
//     }
//     const handleResize = () => {
//       setHeight(window.innerHeight);
//     };
//     window.addEventListener("resize", handleResize);
//     handleResize();
//     return () => {
//       window.removeEventListener("resize", handleResize);
//     };
//   }, [key]);

//   // Parse selectedData to populate initial values
//   const getInitialValues = () => {
//     if (!selectedData) {
//       return {
//         clientName: "",
//         clientMobile: "",
//         status: "",
//         followUpDate: null,
//         // time: "",
//         note: "",
//         commissionAmount: "",
//         commissionPaidBy: "",
//         buyerCommission: "",
//         sellerCommission: "",
//       };
//     }

//     // Parse follow-up date and time
//     let followupDate = null;
//     let followupTime = "";

//     if (selectedData.followUpDate) {
//       const dateObj = new Date(selectedData.followUpDate);
//       followupDate = dateObj;
//       followupTime = dateObj.toTimeString().slice(0, 5); // HH:MM format
//     }

//     return {
//       clientName: selectedData.clientName || "",
//       clientMobile: selectedData.clientMobile || "",
//       status: selectedData.status || "",
//       followUpDate: followupDate,
//       followupTime: followupTime,
//       note: selectedData.note || "",
//       commissionAmount: selectedData.commissionAmount || "",
//       commissionPaidBy: selectedData.commissionPaidBy || "",
//       buyerCommission: selectedData.buyerCommission || "",
//       sellerCommission: selectedData.sellerCommission || "",

//       tenantBudget:
//         Number(selectedData?.tenantBudget) > 0
//           ? Number(selectedData?.tenantBudget)
//           : "",
//       ownerExpected:
//         Number(selectedData?.ownerExpected) > 0
//           ? Number(selectedData?.ownerExpected)
//           : "",
//       negotiationStatus: selectedData?.negotiationStatus || "",

//       monthlyRent:
//         Number(selectedData?.monthlyRent) > 0
//           ? Number(selectedData?.monthlyRent)
//           : "",

//       ownerPayout:
//         Number(selectedData?.ownerShare) > 0
//           ? Number(selectedData?.ownerShare)
//           : "",

//       securityDeposit:
//         Number(selectedData?.securityDeposit) > 0
//           ? Number(selectedData?.securityDeposit)
//           : "",

//       dueDate: selectedData?.dueDate ? new Date(selectedData.dueDate) : null,

//       agreementPeriod:
//         Number(selectedData?.agreementPeriod) > 0
//           ? Number(selectedData?.agreementPeriod)
//           : "",
//     };
//   };

//   // Base validation schema
//   const baseValidationSchema = Yup.object({
//     status: Yup.number().required("Stage is required"),
//   });

//   const validationSchema = baseValidationSchema
//     .shape({
//       followUpDate: Yup.date().when("status", {
//         is: (val) =>
//           val &&
//           (val.toString() === Config.DEAL_STATUS.VISIT_SCHEDULED ||
//             val.toString() === Config.DEAL_STATUS.INQUIRY ||
//             val.toString() === Config.DEAL_STATUS.NEGOTIATION),
//         then: (schema) =>
//           schema
//             .nullable()
//             .required("Follow-up date is required for this stage"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       // ---------------- Brokerage ----------------

//       // commissionAmount: Yup.number().when("status", {
//       //   is: (val) => val && val.toString() === Config.DEAL_STATUS.BOOKED,
//       //   then: (schema) =>
//       //     schema
//       //       .required("Commission amount is required")
//       //       .positive("Invalid amount"),
//       //   otherwise: (schema) => schema.nullable(),
//       // }),

//       commissionPaidBy: Yup.number().when("status", {
//         is: (status) =>
//             Number(status) === Number(Config.DEAL_STATUS.BOOKED),
//         then: (schema) => schema.required("Paid by is required"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       buyerCommission: Yup.number().when(["status", "commissionPaidBy"], {
//         is: (status, commissionPaidBy) =>
//           status &&
//           Number(status) === Number(Config.DEAL_STATUS.BOOKED)&&
//           (commissionPaidBy === Config.PAID_BY_TYPE.BUYER ||
//             commissionPaidBy === Config.PAID_BY_TYPE.BOTH),
//         then: (schema) =>
//           schema
//             .typeError("Buyer amount required")
//             .required("Buyer amount required")
//             .min(0, "Amount cannot be negative"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       sellerCommission: Yup.number().when(["status", "commissionPaidBy"], {
//         is: (status, commissionPaidBy) =>
//           status &&
//           Number(status) === Number(Config.DEAL_STATUS.BOOKED) &&
//           (commissionPaidBy === Config.PAID_BY_TYPE.SELLER ||
//             commissionPaidBy === Config.PAID_BY_TYPE.BOTH),
//         then: (schema) =>
//           schema
//             .typeError("Seller amount required")
//             .required("Seller amount required")
//             .min(0, "Amount cannot be negative"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       // ---------------- Negotiation ----------------
//       tenantBudget: Yup.number().when("status", {
//         is: (val) => Number(val) === Number(Config.DEAL_STATUS.NEGOTIATION),
//         then: (schema) =>
//           schema
//             .typeError("Tenant budget is required")
//             .required("Tenant budget is required")
//             .min(500, "Budget must be at least ₹500")
//             .max(1000000, "Budget cannot exceed ₹10,00,000"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       ownerExpected: Yup.number().when(["status", "tenantBudget"], {
//         is: (status, tenantBudget) =>
//           Number(status) === Number(Config.DEAL_STATUS.NEGOTIATION) &&
//           !!tenantBudget,
//         then: (schema) =>
//           schema
//             .typeError("Owner expectation is required")
//             .required("Owner expectation is required")
//             .min(
//               Yup.ref("tenantBudget"),
//               "Owner expectation cannot be less than tenant budget",
//             )
//             .max(1000000, "Amount cannot exceed ₹10,00,000"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       // ---------------- Lease ----------------
//       monthlyRent: Yup.number().when("status", {
//         is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
//         then: (schema) =>
//           schema
//             .typeError("Tenant rent is required")
//             .required("Tenant rent is required")
//             .min(500, "Rent must be at least ₹500")
//             .max(1000000, "Rent cannot exceed ₹10,00,000"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       ownerPayout: Yup.number().when("status", {
//         is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
//         then: (schema) =>
//           schema
//             .typeError("Owner payout is required")
//             .required("Owner payout is required")
//             .min(0, "Owner payout cannot be negative")
//             .max(1000000, "Owner payout cannot exceed ₹10,00,000"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       securityDeposit: Yup.number().when("status", {
//         is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
//         then: (schema) =>
//           schema
//             .typeError("Security deposit is required")
//             .required("Security deposit is required")
//             .min(0, "Security deposit cannot be negative")
//             .max(5000000, "Deposit cannot exceed ₹50,00,000"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       dueDate: Yup.date().when("status", {
//         is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
//         then: (schema) => schema.nullable().required("Due date is required"),
//         otherwise: (schema) => schema.nullable(),
//       }),

//       agreementPeriod: Yup.number().when("status", {
//         is: (val) => val && Number(val) === Config.DEAL_STATUS.ACTIVE_LEASE,
//         then: (schema) =>
//           schema
//             .typeError("Upcoming rent count is required")
//             .required("No. of upcoming rent is required")
//             .min(1, "Minimum 1 rent cycle required")
//             .max(36, "Maximum 36 months allowed"),
//         otherwise: (schema) => schema.nullable(),
//       }),
//     })
//     .test(
//       "commission-total-check",
//       "Total received amount cannot be greater than commission amount",
//       function (values) {
//         if (
//           !values.status ||
//           values.status.toString() !== Config.DEAL_STATUS.BOOKED
//         )
//           return true;

//         const total =
//           (Number(values.buyerCommission) || 0) +
//           (Number(values.sellerCommission) || 0);

//         return total <= Number(values.commissionAmount || 0);
//       },
//     )
//     .test(
//       "lease-margin-check",
//       "Owner payout cannot be greater than tenant rent",
//       function (values) {
//         if (
//           !values.status ||
//           values.status.toString() !== Config.DEAL_STATUS.ACTIVE_LEASE
//         )
//           return true;

//         const monthlyRent = Number(values.monthlyRent || 0);
//         const ownerPayout = Number(values.ownerPayout || 0);

//         return ownerPayout <= monthlyRent;
//       },
//     );

//   const handleSubmit = (values, formikHelpers) => {
//     let followUpDate = null;

//     if (values.followUpDate) {
//       followUpDate = moment(values.followUpDate).format("YYYY-MM-DD HH:mm:ss");
//     }

//     editDealMutate({
//       dealId: selectedData?.id,
//       stage: values.status,
//       budget: selectedData.budget,
//       date: followUpDate,
//       note: values.note,

//       // Brokerage
//       commissionPaidBy: values.commissionPaidBy || null,
//       buyerCommission: values.buyerCommission || null,
//       sellerCommission: values.sellerCommission || null,

//       // Financials
//       monthlyRent: values.monthlyRent,
//       ownerShare: values.ownerPayout,

//       // Lease Terms
//       securityDeposit: values.securityDeposit,
//       agreementPeriod: values.agreementPeriod,
//       dueDate: values.dueDate
//         ? moment(values.dueDate).format("YYYY-MM-DD")
//         : null,
//       rentalCycle: values.dueDate ? moment(values.dueDate).format("DD") : null,
//       moveInDate: values.dueDate
//         ? moment(values.dueDate).format("YYYY-MM-DD")
//         : null,
//       agreementStartDate: values.dueDate
//         ? moment(values.dueDate).format("YYYY-MM-DD")
//         : null,
//       dealName: selectedData?.name || "",
//       dealMobile: selectedData?.mobile,

//       //Negotitation Data
//       tenantBudget: values.tenantBudget || null,
//       ownerExpected: values.ownerExpected || null,
//       negotiationStage: values.negotiationStatus || null,
//     });
//     // handleClose();
//   };
//   // Map negotiation status to number
//   const DEAL_NEGOTIATION_STATUS = {
//     ONGOING: 1,
//     TENANT_AGREED: 2,
//     OWNER_AGREED: 3,
//     FINAL_DISCUSSION: 4,
//   };
//   return (
//     <Container style={{ height: `${height}px`, overflow: "auto" }}>
//       <ModalHeader>
//         <h1 className="text-white text-lg font-medium">Edit Deal</h1>
//         <button onClick={handleClose}>
//           <MdClose color="white" size={24} />
//         </button>
//       </ModalHeader>

//       <ProfileSection className="px-4 py-3 flex items-center justify-between">
//         {/* Left: Icon + Info */}
//         <div className="flex items-center gap-2">
//           {/* Inventory Icon */}
//           <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center">
//             <FaRegBuilding className="text-gray-600" />
//           </div>

//           {/* Text Info */}
//           <div className="flex flex-col">
//             <p className="text-md font-medium text-gray-800">
//               {capitalizeFirstLetter(selectedData?.name || "")}
//             </p>
//             <p className="text-xs text-gray-400">
//               {capitalizeFirstLetter(selectedData?.inventoryName || "")}
//             </p>
//           </div>
//         </div>

//         {/* Right: Budget */}
//         <div className="text-right">
//           <p className="text-xs text-gray-400">Budget</p>
//           <p className="text-lg font-bold text-green-600">
//             ₹{selectedData?.budget || 0}
//           </p>
//         </div>
//       </ProfileSection>

//       <div className="px-6 h-full flex flex-col justify-between">
//         <Formik
//           innerRef={formikRef}
//           initialValues={getInitialValues()}
//           onSubmit={handleSubmit}
//           validationSchema={validationSchema}
//           validateOnChange={true}
//           validateOnBlur={true}
//           enableReinitialize={true}
//         >
//           {({
//             values,
//             setFieldValue,
//             handleChange,
//             handleBlur,
//             errors,
//             touched,
//             resetForm,
//           }) => {
//             const getCurrentStageLabel = () => {
//               const selectedStage = editStageOptions.find(
//                 (role) => role.value === values?.status,
//               );
//               return selectedStage ? selectedStage.label : "Select stage";
//             };

//             const handleStageSelect = (selectedValue) => {
//               setFieldValue("status", selectedValue);
//               setIsStageModalOpen(false);

//               setFieldValue("note", "");

//               // Reset commission fields if stage is not Booked
//               if (selectedValue !== Config.DEAL_STATUS.BOOKED) {
//                 setFieldValue("commissionAmount", "");
//                 setFieldValue("commissionPaidBy", "");
//                 setFieldValue("buyerCommission", "");
//                 setFieldValue("sellerCommission", "");
//               }

//               // Reset time field if stage is not Visit Scheduled
//               if (selectedValue !== Config.DEAL_STATUS.VISIT_SCHEDULED) {
//                 setFieldValue("followupTime", "");
//               }
//             };

//             return (
//               <Form>
//                 <ScrollableContent className="max-h-[calc(100vh-180px)]">
//                   <Wrapper>
//                     {/* Stage with Selection Modal */}
//                     <InputGroup>
//                       <Label htmlFor="status">Stage *</Label>
//                       <FieldWrappers onClick={() => setIsStageModalOpen(true)}>
//                         <Field
//                           type="text"
//                           name="status"
//                           id="status"
//                           autoComplete="off"
//                           readOnly
//                           value={getCurrentStageLabel()}
//                           className="w-full border-none outline-none bg-transparent cursor-pointer"
//                         />
//                         <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
//                       </FieldWrappers>
//                       {errors.status && touched.status && (
//                         <ErrorText>{errors.status}</ErrorText>
//                       )}
//                     </InputGroup>

//                     {/* Follow-up Date - Required for Inquiry, Visit Scheduled, Negotiation */}
//                     {(values.status === Config.DEAL_STATUS.INQUIRY ||
//                       values.status === Config.DEAL_STATUS.VISIT_SCHEDULED ||
//                       values.status === Config.DEAL_STATUS.NEGOTIATION) && (
//                       <>
//                         <InputGroup>
//                           <Label htmlFor="followUpDate">
//                             Follow-up / Visit Date *
//                           </Label>
//                           <FieldWrappers>
//                             <DatePicker
//                               selected={values.followUpDate}
//                               onChange={(date) =>
//                                 setFieldValue("followUpDate", date)
//                               }
//                               showTimeSelect
//                               timeIntervals={15}
//                               timeCaption="Time"
//                               dateFormat="dd MMM yyyy h:mm aa"
//                               minDate={new Date()}
//                               placeholderText="Select follow-up date & time"
//                               className="w-full"
//                               calendarClassName="custom-datepicker"
//                               popperClassName="custom-datepicker-popper"
//                               popperPlacement="bottom-start"
//                               portalId="root"
//                             />
//                           </FieldWrappers>
//                           {errors.followUpDate && touched.followUpDate && (
//                             <ErrorText>{errors.followUpDate}</ErrorText>
//                           )}
//                         </InputGroup>
//                       </>
//                     )}

//                     {values.status === Config.DEAL_STATUS.NEGOTIATION && (
//                       <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-4">
//                         <div className="flex items-center justify-between">
//                           <p className="text-sm font-semibold text-gray-700">
//                             Negotiation Details
//                           </p>
//                         </div>

//                         {/* Rent Expectation */}
//                         <div className="grid grid-cols-2 gap-3">
//                           <div>
//                             <Label className="mb-1 block text-xs">
//                               Tenant Budget
//                             </Label>
//                             <FieldWrappers>
//                               <RupeeIcon />
//                               <Field
//                                 type="number"
//                                 name="tenantBudget"
//                                 placeholder="Tenant offer"
//                                 className="pl-5 w-full"
//                               />
//                             </FieldWrappers>
//                             {errors.tenantBudget && touched.tenantBudget && (
//                               <ErrorText className="mt-1">
//                                 {errors.tenantBudget}
//                               </ErrorText>
//                             )}
//                           </div>

//                           <div>
//                             <Label className="mb-1 block text-xs">
//                               Owner Expectation
//                             </Label>
//                             <FieldWrappers>
//                               <RupeeIcon />
//                               <Field
//                                 type="number"
//                                 name="ownerExpected"
//                                 placeholder="Owner demand"
//                                 className="pl-5 w-full"
//                               />
//                             </FieldWrappers>
//                             {errors.ownerExpected && touched.ownerExpected && (
//                               <ErrorText className="mt-1">
//                                 {errors.ownerExpected}
//                               </ErrorText>
//                             )}
//                           </div>
//                         </div>

//                         {/* Gap Indicator */}
//                         {values.tenantBudget != null &&
//                           values.ownerExpected != null && (
//                             <div className="bg-white border rounded-lg p-3 text-sm">
//                               <span className="text-gray-500">
//                                 Negotiation Gap
//                               </span>
//                               <p className="text-orange-600 font-semibold">
//                                 ₹{" "}
//                                 {Math.abs(
//                                   (values.ownerExpected || 0) -
//                                     (values.tenantBudget || 0),
//                                 )}
//                               </p>
//                             </div>
//                           )}

//                         <div>
//                           <Label className="mb-1 block text-xs">
//                             Negotiation Status
//                           </Label>
//                           <div className="grid grid-cols-2 gap-2">
//                             {[
//                               "ONGOING",
//                               "OWNER_AGREED",
//                               "TENANT_AGREED",
//                               "FINAL_DISCUSSION",
//                             ].map((option) => (
//                               <button
//                                 key={option}
//                                 type="button"
//                                 onClick={() =>
//                                   setFieldValue(
//                                     "negotiationStatus",
//                                     DEAL_NEGOTIATION_STATUS[option],
//                                   )
//                                 }
//                                 className={`py-2 rounded-lg text-sm font-medium transition
//                                                                 ${
//                                                                   values.negotiationStatus ===
//                                                                   DEAL_NEGOTIATION_STATUS[
//                                                                     option
//                                                                   ]
//                                                                     ? "bg-primary-color text-white"
//                                                                     : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
//                                                                 }`}
//                               >
//                                 {option.replace("_", " ")}{" "}
//                                 {/* optional: nicer display */}
//                               </button>
//                             ))}
//                           </div>
//                         </div>
//                       </div>
//                     )}

//                     {/* Commission Fields - Only for Closed stage */}
//                     {values.status === Config.DEAL_STATUS.BOOKED && (
//                       <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-4">
//                         {/* Header */}
//                         <div className="flex items-center justify-between">
//                           <p className="text-sm font-semibold text-gray-700">
//                             Commission
//                           </p>
//                           <span className="text-xs text-gray-400">
//                             Required
//                           </span>
//                         </div>

//                         {/* Paid By Selection */}
//                         <div>
//                           <Label className="mb-2 block">Who pays?</Label>

//                           <div className="grid grid-cols-3 gap-2">
//                             {paidByOptions.map((option) => (
//                               <button
//                                 key={option.value}
//                                 type="button"
//                                 onClick={() => {
//                                   setFieldValue(
//                                     "commissionPaidBy",
//                                     option.value,
//                                   );
//                                   setFieldValue("buyerCommission", "");
//                                   setFieldValue("sellerCommission", "");
//                                 }}
//                                 className={`py-2 rounded-lg text-sm font-medium transition-all duration-200
//                                     ${
//                                         values.commissionPaidBy === option.value
//                                         ? "bg-primary-color text-white shadow-sm"
//                                         : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-100"
//                                     }
//                                     `}
//                               >
//                                 {option.label}
//                               </button>
//                             ))}
//                           </div>

//                           {errors.commissionPaidBy &&
//                             touched.commissionPaidBy && (
//                               <ErrorText className="mt-1">
//                                 {errors.commissionPaidBy}
//                               </ErrorText>
//                             )}
//                         </div>

//                         {/* Amount Inputs */}
//                         <div className="grid grid-cols-2 gap-3">
//                           {/* Buyer */}
//                           {(values.commissionPaidBy ===
//                             Config.PAID_BY_TYPE.BUYER ||
//                             values.commissionPaidBy ===
//                               Config.PAID_BY_TYPE.BOTH) && (
//                             <div>
//                               <Label className="mb-1 block text-xs">
//                                 Buyer
//                               </Label>
//                               <FieldWrappers>
//                                 <RupeeIcon />
//                                 <Field
//                                   type="number"
//                                   name="buyerCommission"
//                                   className="pl-5 w-full"
//                                   placeholder="Enter commission"
//                                 />
//                               </FieldWrappers>
//                               {errors.buyerCommission &&
//                                 touched.buyerCommission && (
//                                   <ErrorText className="mt-1">
//                                     {errors.buyerCommission}
//                                   </ErrorText>
//                                 )}
//                             </div>
//                           )}

//                           {/* Seller */}
//                           {(values.commissionPaidBy ===
//                             Config.PAID_BY_TYPE.SELLER ||
//                             values.commissionPaidBy ===
//                               Config.PAID_BY_TYPE.BOTH) && (
//                             <div>
//                               <Label className="mb-1 block text-xs">
//                                 Seller
//                               </Label>
//                               <FieldWrappers>
//                                 <RupeeIcon />
//                                 <Field
//                                   type="number"
//                                   name="sellerCommission"
//                                   className="pl-5 w-full"
//                                   placeholder="Enter commission"
//                                 />
//                               </FieldWrappers>
//                               {errors.sellerCommission &&
//                                 touched.sellerCommission && (
//                                   <ErrorText className="mt-1">
//                                     {errors.sellerCommission}
//                                   </ErrorText>
//                                 )}
//                             </div>
//                           )}
//                         </div>

//                         {/* Validation Message */}
//                         {values.commissionAmount &&
//                           Number(values.buyerCommission || 0) +
//                             Number(values.sellerCommission || 0) >
//                             Number(values.commissionAmount) && (
//                             <div className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded-md">
//                               Total received amount cannot exceed commission
//                               amount
//                             </div>
//                           )}
//                       </div>
//                     )}

//                     {values.status === Config.DEAL_STATUS.ACTIVE_LEASE && (
//                       <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-4">
//                         {/* Lease Financials */}
//                         <div className="flex items-center justify-between">
//                           <p className="text-sm font-semibold text-gray-700">
//                             Lease Financials
//                           </p>
//                           <span className="text-xs text-gray-400">Monthly</span>
//                         </div>

//                         <div className="grid grid-cols-2 gap-3">
//                           <div>
//                             <Label className="mb-1 block text-xs">
//                               Tenant Rent
//                             </Label>
//                             <FieldWrappers>
//                               <RupeeIcon />
//                               <Field
//                                 type="number"
//                                 name="monthlyRent"
//                                 placeholder="Enter rent"
//                                 className="pl-5 w-full"
//                               />
//                             </FieldWrappers>
//                             {errors.monthlyRent && touched.monthlyRent && (
//                               <ErrorText>{errors.monthlyRent}</ErrorText>
//                             )}
//                           </div>

//                           <div>
//                             <Label className="mb-1 block text-xs">
//                               Owner Payout
//                             </Label>
//                             <FieldWrappers>
//                               <RupeeIcon />
//                               <Field
//                                 type="number"
//                                 name="ownerPayout"
//                                 placeholder="Enter payout"
//                                 className="pl-5 w-full"
//                               />
//                             </FieldWrappers>
//                             {errors.ownerPayout && touched.ownerPayout && (
//                               <ErrorText>{errors.ownerPayout}</ErrorText>
//                             )}
//                           </div>
//                         </div>

//                         {/* Auto Margin */}
//                         <div className="bg-white border rounded-lg p-3 text-sm">
//                           <span className="text-gray-500">Your Margin</span>
//                           <p className="text-green-600 font-semibold">
//                             ₹{" "}
//                             {(values.monthlyRent || 0) -
//                               (values.ownerPayout || 0)}
//                           </p>
//                         </div>

//                         {/* -------- Lease Terms -------- */}
//                         <div className="border-t pt-4 space-y-4">
//                           <div className="flex items-center justify-between">
//                             <p className="text-sm font-semibold text-gray-700">
//                               Lease Terms
//                             </p>
//                             <span className="text-xs text-gray-400">
//                               Required
//                             </span>
//                           </div>

//                           {/* Security Deposit */}
//                           <div>
//                             <Label className="mb-1 block text-xs">
//                               Security Deposit
//                             </Label>
//                             <FieldWrappers>
//                               <RupeeIcon />
//                               <Field
//                                 type="number"
//                                 name="securityDeposit"
//                                 placeholder="Enter deposit"
//                                 className="pl-5 w-full"
//                               />
//                             </FieldWrappers>
//                             {errors.securityDeposit &&
//                               touched.securityDeposit && (
//                                 <ErrorText>{errors.securityDeposit}</ErrorText>
//                               )}
//                           </div>

//                           <div className="grid grid-cols-2 gap-3">
//                             {/* Due Date */}
//                             <div>
//                               <Label>Due Date</Label>
//                               <FieldWrappers>
//                                 <DatePicker
//                                   selected={values.dueDate}
//                                   onChange={(d) => setFieldValue("dueDate", d)}
//                                   dateFormat="yyyy-MM-dd"
//                                   placeholderText="Select date"
//                                   className="cursor-pointer items-center"
//                                   showIcon
//                                   icon={
//                                     <FaRegCalendar className="text-gray-400 text-xs mt-0.5" />
//                                   }
//                                 />
//                               </FieldWrappers>
//                               {errors.dueDate && touched.dueDate && (
//                                 <ErrorText>{errors.dueDate}</ErrorText>
//                               )}
//                             </div>

//                             {/* No. of Upcoming Payments */}
//                             <div>
//                               {/* <Label className="mb-1 block text-xs">No. of Rent Payments</Label>
//                                                             <FieldWrappers>
//                                                                 <Field
//                                                                     type="number"
//                                                                     name="agreementPeriod"
//                                                                     placeholder="e.g. 6"
//                                                                     className="w-full"
//                                                                     min="1"
//                                                                 />
//                                                             </FieldWrappers> */}
//                               <Label>No. of Upcoming Rent</Label>
//                               <FieldWrappers>
//                                 <Field
//                                   type="number"
//                                   name="agreementPeriod"
//                                   placeholder="Enter months"
//                                   className="w-full"
//                                 />
//                               </FieldWrappers>
//                               {errors.agreementPeriod &&
//                                 touched.agreementPeriod && (
//                                   <ErrorText>
//                                     {errors.agreementPeriod}
//                                   </ErrorText>
//                                 )}
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     )}

//                     <InputGroup>
//                       <Label htmlFor="note">
//                         {values.status === Config.DEAL_STATUS.LOST
//                           ? "Reason *"
//                           : "Notes"}
//                       </Label>
//                       <Textarea
//                         id="note"
//                         name="note"
//                         className="w-full h-20"
//                         placeholder="Enter your remarks..."
//                         value={values.note}
//                         onChange={handleChange}
//                         onBlur={handleBlur}
//                       />
//                       {errors.note && touched.note && (
//                         <ErrorText>{errors.note}</ErrorText>
//                       )}
//                     </InputGroup>
//                   </Wrapper>
//                 </ScrollableContent>

//                 <ActionSection>
//                   <SideCancelBtn
//                     type="button"
//                     onClick={() => {
//                       resetForm();
//                       handleClose();
//                     }}
//                   >
//                     Cancel
//                   </SideCancelBtn>
//                   <SubmitBtn type="submit">
//                     {editDealLoading ? "Updating..." : "Update"}
//                   </SubmitBtn>
//                 </ActionSection>

//                 <SelectOptionModal
//                   isOpen={isStageModalOpen}
//                   onClose={() => setIsStageModalOpen(false)}
//                   title="Select Stage"
//                   options={stageOption}
//                   selectedValue={values.status}
//                   onSelect={handleStageSelect}
//                   searchPlaceholder="Search stage..."
//                 />
//               </Form>
//             );
//           }}
//         </Formik>
//       </div>
//     </Container>
//   );
// };

// const Container = tw.div`bg-white w-full h-full flex flex-col`;
// const Header = tw.div`flex justify-between items-center bg-[#26A69A] px-4 py-4`;
// const Textarea = tw.textarea`w-full h-24 border rounded-xl p-2`;
// const ErrorText = tw.div`text-red-500 text-xs`;
// const Summary = tw.div`flex items-center`;
// const SummaryItem = tw.div`flex flex-col items-end`;

// export default EditDeal;
